import React, { useEffect, useMemo, useState } from 'react';
import {
  BarList, Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select,
  SubTabs, TextInput, fmtCell, fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import { AdminLink } from './adminNav';

/**
 * AI Labs — TRL Lab v2 (two-pass assessment) and the Horizon Sandbox,
 * migrated from admin_v3/trl_lab.py and admin_v3/horizon.py.
 *
 * The Streamlit versions ran their batches inside the page and drove a
 * progress bar with st.rerun, so closing the tab killed the run. Here a run is
 * a server-side job: start it, watch the progress poll, come back later. The
 * legacy single-pass TRL lab is not carried over — it was already marked
 * superseded by the two-pass engine in its own header.
 */

const TABS = ['TRL Lab v2', 'Horizon Sandbox'];

const LabsPanel: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  const config = useAdminGet('labs', 'config');
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {config.data && !config.data.azure_configured && (
        <Msg kind="error">
          Azure OpenAI is not configured, so new runs will be refused. Existing results below are
          unaffected. Add the key under System → Integrations (or set AZURE_OPENAI_API_KEY and
          AZURE_OPENAI_ENDPOINT in Backend/.env).
        </Msg>
      )}
      {tab === 'TRL Lab v2' && <TrlLab config={config.data} />}
      {tab === 'Horizon Sandbox' && <Horizon config={config.data} />}
    </div>
  );
};

/** Polls a running job until it finishes; renders a progress bar. */
const JobProgress: React.FC<{ jobId: string; onDone: () => void }> = ({ jobId, onDone }) => {
  const [job, setJob] = useState<any>(null);
  useEffect(() => {
    let live = true;
    const tick = async () => {
      const { data } = await panelGet('labs', 'job', { job_id: jobId });
      if (!live) return;
      setJob(data);
      if (data && (data.status === 'done' || data.status === 'failed')) { onDone(); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  if (!job) return <Loading label="Starting…" />;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs text-grayscale-1100">
        <span>{job.status === 'running' ? (job.note || 'Working…') : job.status}</span>
        <span>{job.done}/{job.total}</span>
      </div>
      <div className="bg-grayscale-200 rounded-full h-2 overflow-hidden">
        <div className="h-2 bg-primary-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      {job.error && <Msg kind="error">{job.error}</Msg>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TRL LAB v2
// ---------------------------------------------------------------------------
const TrlLab: React.FC<{ config: any }> = ({ config }) => {
  const [sub, setSub] = useState('Run');
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={['Run', 'Results', 'Statistics', 'How it works']} active={sub} onChange={setSub} />
      {sub === 'Run' && <TrlRun />}
      {sub === 'Results' && <TrlResults />}
      {sub === 'Statistics' && <TrlStats />}
      {sub === 'How it works' && <TrlHelp config={config} />}
    </div>
  );
};

const TrlRun: React.FC = () => {
  const unis = useAdminGet('labs', 'trl_universities');
  const jobs = useAdminGet('labs', 'trl_jobs');
  const [count, setCount] = useState('5');
  const [uni, setUni] = useState('All Universities');
  const [onlyWithout, setOnlyWithout] = useState(true);
  const [preview, setPreview] = useState<any[] | null>(null);
  const [job, setJob] = useState('');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const sample = async () => {
    setMsg(null);
    const { data, error } = await panelGet('labs', 'trl_sample', {
      count, university: uni === 'All Universities' ? '' : uni,
      only_without_trl: String(onlyWithout),
    });
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setPreview(data || []);
      if (!data?.length) setMsg({ kind: 'error', text: 'No technologies matched those filters.' });
    }
  };

  const run = async () => {
    setMsg(null);
    const { data, error } = await panelPost('labs', 'trl_run', {
      count: Number(count), university: uni === 'All Universities' ? '' : uni,
      only_without_trl: onlyWithout,
    });
    if (error) setMsg({ kind: 'error', text: error });
    else { setJob(data.job_id); setPreview(data.technologies || []); }
  };

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Run a 2-pass TRL assessment">
        <div className="flex gap-2 flex-wrap items-center">
          <label className="text-xs text-grayscale-1100 flex items-center gap-2">
            Technologies
            <input type="number" min={1} max={50} value={count} onChange={e => setCount(e.target.value)}
              className="h-9 w-20 px-2 rounded-md border-[0.5px] border-primary-300 text-sm" />
          </label>
          <Select value={uni} onChange={setUni} options={unis.data || ['All Universities']} />
          <label className="text-xs text-grayscale-1200 flex items-center gap-1.5">
            <input type="checkbox" checked={onlyWithout} onChange={e => setOnlyWithout(e.target.checked)} />
            Only techs with no original TRL
          </label>
          <Btn small onClick={sample}>Preview sample</Btn>
          <Btn small kind="primary" onClick={run}>Run assessment</Btn>
        </div>
        {job && <JobProgress jobId={job} onDone={() => { jobs.reload(); }} />}
        {preview && preview.length > 0 && (
          <DataTable rows={preview} maxHeight="16rem"
            columns={['utech_technology_id', 'university_name', 'title']} />
        )}
      </Section>
      <Section title="Recent runs" right={<Btn small onClick={jobs.reload}>Refresh</Btn>}>
        {jobs.loading ? <Loading /> : (
          <DataTable rows={jobs.data || []} csvName="trl_runs"
            columns={['created_at', 'status', 'total', 'done', 'saved', 'admin_email', 'error']}
            empty="No TRL runs yet." />
        )}
      </Section>
    </div>
  );
};

/** A crashed model call is not a verdict — it must never read as "Not Disclosed". */
const FailedBadge: React.FC<{ error: string }> = ({ error }) => (
  <span title={error}
    className="text-xs rounded-full px-2 py-0.5 bg-red-50 text-red-700 border border-red-200">
    Failed
  </span>
);

const TrlResults: React.FC = () => {
  const [status, setStatus] = useState('All');
  const results = useAdminGet('labs', 'trl_results', { review_status: status }, [status]);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const clear = async () => {
    const { data, error } = await panelPost('labs', 'trl_clear', {});
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `Deleted ${data.deleted} result(s).` }); results.reload(); }
  };

  // A crashed call is not a verdict. Rewriting final_trl (rather than only
  // painting the cell) keeps the row filter, the cell tooltip and the CSV export
  // agreeing with the badge instead of exporting a "Not Disclosed" that no model
  // ever produced. Rows written before the column existed have no `error` and
  // pass through untouched.
  const rows: any[] = (results.data || []).map((r: any) =>
    (r.error ? { ...r, final_trl: 'Failed' } : r));

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Assessment results"
        right={<div className="flex gap-2 items-center">
          <Select value={status} onChange={setStatus} options={['All', 'pending', 'approved', 'rejected']} />
          <Btn small onClick={results.reload}>Refresh</Btn>
          <DangerBtn small onConfirm={clear}>Clear all results</DangerBtn>
        </div>}>
        {results.loading ? <Loading /> : (
          <DataTable rows={rows} searchable csvName="trl_v2_results"
            columns={['created_at', 'utech_technology_id', 'university_name', 'final_trl',
              'pass1_confidence', 'pass2_confidence', 'review_status', 'corrected_trl']}
            labels={{ pass1_confidence: 'P1 conf', pass2_confidence: 'P2 conf' }}
            empty="No assessments yet — run one from the Run tab."
            render={{ final_trl: r => (r.error
              ? <FailedBadge error={String(r.error)} />
              : fmtCell('final_trl', r.final_trl)) }}
            actions={r => <Btn small onClick={() => setOpen(r.id)}>Open</Btn>} />
        )}
      </Section>
      {open && <TrlDetail id={open} onClose={() => setOpen(null)} onReviewed={results.reload} />}
    </div>
  );
};

const TrlDetail: React.FC<{ id: string; onClose: () => void; onReviewed: () => void }> =
  ({ id, onClose, onReviewed }) => {
  const exp = useAdminGet('labs', 'trl_experiment', { id }, [id]);
  const [feedback, setFeedback] = useState('');
  const [corrected, setCorrected] = useState('');
  const [showText, setShowText] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const d = exp.data;

  const review = async (status: string) => {
    const { error } = await panelPost('labs', 'trl_review',
      { id, status, feedback, corrected_trl: status === 'rejected' ? corrected : '' });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `Marked ${status}.` }); onReviewed(); exp.reload(); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-2xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">
            {d?.utech_technology_id || '…'}
          </h2>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {exp.loading ? <Loading /> : d && (
          <>
            <Section title="Verdict">
              <div className="text-sm text-grayscale-1300">
                {d.error ? (
                  <>
                    <div className="text-2xl font-bold font-grotesk text-red-700">Assessment failed</div>
                    <div className="mt-1"><Msg kind="error">{String(d.error)}</Msg></div>
                  </>
                ) : (
                  <div className="text-2xl font-bold font-grotesk">
                    {d.final_trl === 'Not Disclosed' ? 'Not disclosed' : `TRL ${d.final_trl}`}
                  </div>
                )}
                <p className="mt-1">{d.final_statement}</p>
                <div className="text-xs text-grayscale-1100 mt-2">
                  {d.university_name} · {d.title}
                </div>
              </div>
            </Section>
            <Section title="Pass 1 — evidence extraction">
              <KPIRow>
                <KPI label="Confidence" value={`${d.pass1_confidence}/5`} hint="needs ≥3 to continue" />
                <KPI label="Passed" value={d.pass1_passed ? 'yes' : 'no'} />
              </KPIRow>
              <p className="text-sm text-grayscale-1300">{d.pass1_summary}</p>
              <div className="text-sm bg-white rounded-lg border border-grayscale-300 p-3 whitespace-pre-wrap">
                {d.pass1_evidence || '—'}
              </div>
            </Section>
            <Section title="Pass 2 — TRL assignment">
              <KPIRow>
                <KPI label="Assigned" value={d.pass2_trl || '—'} />
                <KPI label="Confidence" value={`${d.pass2_confidence}/5`} hint="needs ≥2 to stand" />
              </KPIRow>
              <p className="text-sm text-grayscale-1300">{d.pass2_statement}</p>
            </Section>
            <Section title={`Source text (${d.technology?.full_text_length ?? 0} chars)`}
              right={<Btn small onClick={() => setShowText(s => !s)}>{showText ? 'Hide' : 'Show'}</Btn>}>
              <div className="text-xs text-grayscale-1100">
                Catalog TRL: original {String(d.technology?.original_trl ?? '—')} ·
                calculated {String(d.technology?.calculated_trl ?? '—')}
              </div>
              {showText && (
                <div className="text-sm bg-white rounded-lg border border-grayscale-300 p-3 max-h-72 overflow-y-auto whitespace-pre-wrap">
                  {d.technology?.full_text || '—'}
                </div>
              )}
            </Section>
            <Section title="Review">
              <div className="flex gap-2 flex-wrap items-center">
                <TextInput value={feedback} onChange={setFeedback} placeholder="Feedback (optional)" wide />
                <Select value={corrected} onChange={setCorrected}
                  options={['', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Not Disclosed']}
                  labels={{ '': 'Corrected TRL…' }} />
                <Btn small kind="primary" onClick={() => review('approved')}>Approve</Btn>
                <Btn small onClick={() => review('rejected')}>Reject</Btn>
              </div>
              <div className="text-xs text-grayscale-1100">
                Current: {d.review_status}{d.reviewer ? ` by ${d.reviewer}` : ''}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

const TrlStats: React.FC = () => {
  const stats = useAdminGet('labs', 'trl_stats');
  const s = stats.data;
  return (
    <div className="flex flex-col gap-4">
      {stats.loading ? <Loading /> : s && (
        <>
          <KPIRow>
            <KPI label="Assessed" value={s.total} />
            <KPI label="TRL disclosed" value={`${s.disclosed_pct}%`} hint={`${s.disclosed} techs`} />
            <KPI label="Pass 1 cleared" value={`${s.pass1_pass_pct}%`} hint={`${s.pass1_passed} techs`} />
            <KPI label="Reviewed" value={s.reviewed} />
            <KPI label="Approval" value={`${s.approval_pct}%`} hint={`${s.approved} approved`} />
            <KPI label="Corrections" value={s.corrections} />
          </KPIRow>
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="TRL distribution">
              <BarList items={Object.entries(s.by_trl || {}).map(([k, v]) => ({ label: k, value: v as number }))} />
            </Section>
            <Section title="Review status">
              <BarList items={Object.entries(s.by_status || {}).map(([k, v]) => ({ label: k, value: v as number }))} />
            </Section>
            <Section title="Pass 1 confidence">
              <BarList items={Object.entries(s.pass1_confidence || {}).map(([k, v]) => ({ label: `score ${k}`, value: v as number }))} />
            </Section>
            <Section title="Pass 2 confidence">
              <BarList items={Object.entries(s.pass2_confidence || {}).map(([k, v]) => ({ label: `score ${k}`, value: v as number }))} />
            </Section>
          </div>
        </>
      )}
    </div>
  );
};

const TrlHelp: React.FC<{ config: any }> = ({ config }) => (
  <div className="grid md:grid-cols-2 gap-4">
    <Section title="How the 2-pass system works">
      <div className="text-sm text-grayscale-1300 flex flex-col gap-2">
        <p><b>Pass 1 — evidence extraction.</b> The model scans the full technology text for direct
          statements about development stage and scores the evidence 0–5. A score below 3 stops
          there and the technology is recorded as "TRL Not Disclosed".</p>
        <p><b>Pass 2 — TRL assignment.</b> Working only from the extracted evidence (never the
          original text), the model assigns a level and scores its own confidence. Below 2 the
          verdict falls back to "Not Disclosed".</p>
        <p className="text-grayscale-1100">The conservatism is deliberate: a wrong TRL is worse than
          an absent one, because it is shown to buyers as a fact.</p>
      </div>
    </Section>
    <Section title="TRL scale">
      <div className="text-sm text-grayscale-1300 flex flex-col gap-1">
        {(config?.trl_scale || []).map((t: any) => (
          <div key={t.level}><b>{t.level}.</b> {t.label}</div>
        ))}
      </div>
      <div className="text-xs text-grayscale-1100">Model: {config?.trl_model}</div>
    </Section>
  </div>
);

// ---------------------------------------------------------------------------
// HORIZON SANDBOX
// ---------------------------------------------------------------------------
/** A saved prompt lifted out of the library and dropped into the Run editor. */
type LoadedPrompt = {
  id: string; name: string; content_hash?: string;
  master_prompt: string; horizon_md: string; problem_md: string;
};

const Horizon: React.FC<{ config: any }> = ({ config }) => {
  const [sub, setSub] = useState('Run & review');
  // Owned here rather than in HzRun: the tabs unmount their panel, so a prompt
  // picked in the library has to survive the hop back to the run editor.
  const [loaded, setLoaded] = useState<LoadedPrompt | null>(null);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SubTabs tabs={['Run & review', 'Prompts', 'Reviewed', 'Leaderboard', 'Runs', 'Summary']}
          active={sub} onChange={setSub} />
        <AdminLink to={{ group: 'horizon' }} className="text-xs text-primary-700 underline underline-offset-2 whitespace-nowrap"
          title="Every prompt, run and result, per-technology coverage (have / pending) and what the tech pages show">
          Open Horizon data →
        </AdminLink>
      </div>
      {sub === 'Run & review' && (
        <HzRun config={config} loaded={loaded} onUnload={() => setLoaded(null)} />
      )}
      {sub === 'Prompts' && <HzPrompts onLoad={p => { setLoaded(p); setSub('Run & review'); }} />}
      {sub === 'Reviewed' && <HzReviewed />}
      {sub === 'Leaderboard' && <HzLeaderboard />}
      {sub === 'Runs' && <HzRuns />}
      {sub === 'Summary' && <HzSummary />}
    </div>
  );
};

const HzRun: React.FC<{ config: any; loaded: LoadedPrompt | null; onUnload: () => void }> =
  ({ config, loaded, onUnload }) => {
  const filters = useAdminGet('labs', 'hz_filters');
  const defaults = useAdminGet('labs', 'hz_defaults');
  const runs = useAdminGet('labs', 'hz_runs');
  const [f, setF] = useState({ country: '', university: '', industry: '', trl: '', require_raw: false });
  const [size, setSize] = useState('5');
  const [allowRe, setAllowRe] = useState(false);
  const [useRawText, setUseRawText] = useState(false);
  const [model, setModel] = useState('gpt-4.1');
  const [promptName, setPromptName] = useState(loaded?.name || '');
  const [prompts, setPrompts] = useState({
    master_prompt: loaded?.master_prompt || '',
    horizon_md: loaded?.horizon_md || '',
    problem_md: loaded?.problem_md || '',
  });
  /** The library entry currently in the editor, or null once reset to defaults. */
  const [fromLibrary, setFromLibrary] = useState<LoadedPrompt | null>(loaded);
  const [preview, setPreview] = useState<any>(null);
  const [job, setJob] = useState('');
  const [runId, setRunId] = useState('');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  // The defaults arrive asynchronously and would otherwise land on top of a
  // prompt the operator just loaded from the library.
  useEffect(() => { if (defaults.data && !fromLibrary) setPrompts(defaults.data); }, [defaults.data, fromLibrary]);

  useEffect(() => {
    if (!loaded) return;
    setPrompts({
      master_prompt: loaded.master_prompt || '',
      horizon_md: loaded.horizon_md || '',
      problem_md: loaded.problem_md || '',
    });
    setPromptName(loaded.name || '');
    setFromLibrary(loaded);
  }, [loaded]);

  const resetPrompts = () => {
    setFromLibrary(null);
    onUnload();  // also at the source, or leaving and re-entering the tab reloads it
    if (defaults.data) setPrompts(defaults.data);
  };

  const doPreview = async () => {
    setMsg(null);
    const { data, error } = await panelGet('labs', 'hz_sample', {
      ...f, require_raw: String(f.require_raw), sample_size: size, allow_reprocess: String(allowRe),
    });
    if (error) setMsg({ kind: 'error', text: error });
    else setPreview(data);
  };

  const run = async () => {
    setMsg(null);
    const { data, error } = await panelPost('labs', 'hz_run', {
      filters: f, sample_size: Number(size), allow_reprocess: allowRe, model,
      use_raw_text: useRawText, prompt_name: promptName, ...prompts,
    });
    if (error) setMsg({ kind: 'error', text: error });
    else { setJob(data.job_id); setRunId(data.run_id); }
  };

  const opt = (v: string[]) => ['', ...v];

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Sample">
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={f.country} onChange={v => setF(s => ({ ...s, country: v }))}
            options={opt(filters.data?.countries || [])} labels={{ '': 'Any country' }} />
          <Select value={f.university} onChange={v => setF(s => ({ ...s, university: v }))}
            options={opt(filters.data?.universities || [])} labels={{ '': 'Any university' }} />
          <Select value={f.industry} onChange={v => setF(s => ({ ...s, industry: v }))}
            options={opt(filters.data?.industries || [])} labels={{ '': 'Any industry' }} />
          <Select value={f.trl} onChange={v => setF(s => ({ ...s, trl: v }))}
            options={['', 'Not disclosed', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
            labels={{ '': 'Any TRL' }} />
          <label className="text-xs text-grayscale-1200 flex items-center gap-1.5"
            title="Eligibility filter only — it does not change what the model is sent.">
            <input type="checkbox" checked={f.require_raw}
              onChange={e => setF(s => ({ ...s, require_raw: e.target.checked }))} />
            Only sample technologies that have full text
          </label>
          <label className="text-xs text-grayscale-1100 flex items-center gap-2">
            Sample
            <input type="number" min={1} max={50} value={size} onChange={e => setSize(e.target.value)}
              className="h-9 w-20 px-2 rounded-md border-[0.5px] border-primary-300 text-sm" />
          </label>
          <Select value={model} onChange={setModel} options={config?.horizon_models || ['gpt-4.1']} />
          <label className="text-xs text-grayscale-1200 flex items-center gap-1.5">
            <input type="checkbox" checked={allowRe} onChange={e => setAllowRe(e.target.checked)} />
            Allow re-processing
          </label>
          <Btn small onClick={doPreview}>Preview</Btn>
        </div>
        {preview && (
          <>
            <div className="text-xs text-grayscale-1100">
              {preview.matched} match these filters · {preview.already_processed?.toLocaleString?.()} technologies
              already classified in an earlier run{allowRe ? ' (re-processing allowed)' : ' and therefore excluded'}
            </div>
            {preview.sample?.length > 0 && (
              <DataTable rows={preview.sample} maxHeight="14rem"
                columns={['utech_technology_id', 'title', 'university_name']} />
            )}
          </>
        )}
      </Section>

      <Section title="Prompt" right={
        <div className="flex gap-2 items-center">
          <TextInput value={promptName} onChange={setPromptName} placeholder="Prompt label…" />
          <Btn small onClick={resetPrompts}>Reset to defaults</Btn>
          <Btn small kind="primary" onClick={run}>Run analysis</Btn>
        </div>}>
        {fromLibrary && (
          <div className="text-xs text-grayscale-1200 bg-primary-100 border border-primary-300 rounded-lg px-3 py-2">
            Loaded from the library: <b>{fromLibrary.name}</b>
            {fromLibrary.content_hash ? ` · ${String(fromLibrary.content_hash).slice(0, 8)}` : ''}
            <div className="text-grayscale-1100">
              Running it unchanged scores against that same library entry; editing the text below
              creates a new one, because prompts are content-hashed. “Reset to defaults” unloads it.
            </div>
          </div>
        )}
        <label className="text-xs text-grayscale-1200 flex items-center gap-1.5">
          <input type="checkbox" checked={useRawText}
            onChange={e => setUseRawText(e.target.checked)} />
          Send full technology text to the model
        </label>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Off by default: each card in the prompt is the catalog summary only. On, the first 6,000
          characters of the scraped page are appended to it. This is what the model receives — the
          “only sample technologies that have full text” filter above only decides which
          technologies are eligible to be drawn.
        </p>
        {(['master_prompt', 'horizon_md', 'problem_md'] as const).map(k => (
          <label key={k} className="flex flex-col gap-1 text-xs text-grayscale-1100">
            {k === 'master_prompt' ? 'Master prompt' : k === 'horizon_md' ? 'Horizon definitions' : 'Problem-type definitions'}
            <textarea rows={k === 'master_prompt' ? 8 : 5} value={prompts[k]}
              onChange={e => setPrompts(s => ({ ...s, [k]: e.target.value }))}
              className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-xs font-mono" />
          </label>
        ))}
        {job && <JobProgress jobId={job} onDone={runs.reload} />}
      </Section>

      {runId && <HzResults runId={runId} />}
      {!runId && <HzResults runId="" />}
    </div>
  );
};

const HzResults: React.FC<{ runId: string }> = ({ runId }) => {
  const runs = useAdminGet('labs', 'hz_runs', { limit: 50 });
  const [pick, setPick] = useState(runId);
  useEffect(() => { if (runId) setPick(runId); }, [runId]);
  const chosen = pick || runs.data?.runs?.[0]?.id || '';
  const results = useAdminGet('labs', 'hz_results', { run_id: chosen, limit: 200 }, [chosen]);
  const [open, setOpen] = useState<any>(null);

  return (
    <Section title="Results" right={
      <Select value={chosen} onChange={setPick}
        options={(runs.data?.runs || []).map((r: any) => r.id)}
        labels={Object.fromEntries((runs.data?.runs || []).map((r: any) =>
          [r.id, `${r.prompt} · ${r.techs} techs · ${String(r.when).slice(0, 10)}`]))} />}>
      {results.loading ? <Loading /> : (
        <DataTable rows={results.data || []} searchable csvName="horizon_results"
          columns={['utech_technology_id', 'tech_title', 'university_name', 'horizon',
            'horizon_confidence', 'primary_problem_type', 'reviewed', 'approved']}
          empty="No results for this run."
          actions={r => <Btn small onClick={() => setOpen(r)}>Review</Btn>} />
      )}
      {open && <HzReview row={open} onClose={() => setOpen(null)} onSaved={results.reload} />}
    </Section>
  );
};

const HzReview: React.FC<{ row: any; onClose: () => void; onSaved: () => void }> =
  ({ row, onClose, onSaved }) => {
  const card = useAdminGet('labs', 'hz_tech_card', { tech_id: row.utech_technology_id },
    [row.utech_technology_id]);
  const [hOk, setHOk] = useState<boolean | null>(null);
  const [pOk, setPOk] = useState<boolean | null>(null);
  const [corrH, setCorrH] = useState('');
  const [corrP, setCorrP] = useState('');
  const [notes, setNotes] = useState(row.review_notes || '');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const save = async (approved: boolean) => {
    const { error } = await panelPost('labs', 'hz_review', {
      id: row.id, correct_horizon: hOk, correct_problem_type: pOk,
      corrected_horizon: corrH, corrected_problem_type: corrP, approved, notes,
    });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: 'Review saved.' }); onSaved(); }
  };

  const tri = (v: boolean | null, set: (b: boolean | null) => void, label: string) => (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-grayscale-1200 w-32">{label}</span>
      <Btn small kind={v === true ? 'primary' : 'subtle'} onClick={() => set(true)}>Correct</Btn>
      <Btn small kind={v === false ? 'danger' : 'subtle'} onClick={() => set(false)}>Wrong</Btn>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-2xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">{row.tech_title}</h2>
            <div className="text-xs text-grayscale-1100">{row.utech_technology_id} · {row.university_name}</div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        <Section title="Model verdict">
          <KPIRow>
            <KPI label="Horizon" value={row.horizon || '—'} hint={row.horizon_confidence} />
            <KPI label="Primary problem" value={row.primary_problem_type || '—'} />
          </KPIRow>
          <div className="text-sm text-grayscale-1300 flex flex-col gap-2">
            <div><b>Inferred problem:</b> {row.inferred_problem || '—'}</div>
            <div><b>Reasoning:</b> {row.reasoning || '—'}</div>
            <div><b>Secondary types:</b> {row.secondary_problem_types || '—'}</div>
            <div><b>Example applications:</b> {row.example_applications || '—'}</div>
          </div>
        </Section>
        <Section title="Technology card">
          {card.loading ? <Loading /> : (
            <div className="text-sm text-grayscale-1300 flex flex-col gap-2">
              <div>{card.data?.summary || '—'}</div>
              <div className="text-xs text-grayscale-1100">Researchers: {card.data?.researchers}</div>
              {card.data?.ip_summary && <div><b>IP:</b> {card.data.ip_summary}</div>}
              {card.data?.publications_summary && <div><b>Publications:</b> {card.data.publications_summary}</div>}
            </div>
          )}
        </Section>
        <Section title="Your verdict">
          {tri(hOk, setHOk, 'Horizon')}
          {tri(pOk, setPOk, 'Problem type')}
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={corrH} onChange={setCorrH} options={['', 'H1', 'H2', 'H3']}
              labels={{ '': 'Corrected horizon…' }} />
            <Select value={corrP} onChange={setCorrP}
              options={['', 'Performance', 'Cost Reduction', 'Sustainability', 'Speed', 'Safety']}
              labels={{ '': 'Corrected problem type…' }} />
          </div>
          <TextInput value={notes} onChange={setNotes} placeholder="Review notes…" wide />
          <div className="flex gap-2">
            <Btn small kind="primary" onClick={() => save(true)}>Approve</Btn>
            <Btn small onClick={() => save(false)}>Reject</Btn>
          </div>
        </Section>
      </div>
    </div>
  );
};

const HzPrompts: React.FC<{ onLoad: (p: LoadedPrompt) => void }> = ({ onLoad }) => {
  const prompts = useAdminGet('labs', 'hz_prompts');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const del = async (id: string) => {
    const { error } = await panelPost('labs', 'hz_prompt_delete', { id });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: 'Prompt deleted.' }); prompts.reload(); }
  };
  return (
    <Section title="Saved prompt library" right={<Btn small onClick={prompts.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Prompts are content-hashed: an identical prompt reuses its entry, so leaderboard scores
        accumulate across runs instead of fragmenting. Loading one drops its three texts into the
        Run editor, so a scored prompt can be re-run or used as the base for the next version.
      </p>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {prompts.loading ? <Loading /> : (
        <DataTable rows={prompts.data || []} searchable csvName="horizon_prompts"
          columns={['name', 'created_at']}
          empty="No saved prompts."
          actions={r => (
            <div className="flex gap-2">
              {/* Fresh object per click so re-picking the same row still reloads it. */}
              <Btn small kind="primary" onClick={() => onLoad({
                id: r.id, name: r.name, content_hash: r.content_hash,
                master_prompt: r.master_prompt || '', horizon_md: r.horizon_md || '',
                problem_md: r.problem_md || '',
              })}>Load into editor</Btn>
              <DangerBtn small onConfirm={() => del(r.id)}>Delete</DangerBtn>
            </div>
          )} />
      )}
    </Section>
  );
};

/**
 * Every reviewed technology, across every run — the legacy cross-run view from
 * horizon.py. hz_results with no run_id and reviewed_only tags each row with the
 * prompt and model that produced it; the three filters are applied client-side
 * over that set, as the Streamlit page did, and their options come from the rows
 * themselves (the catalog holds problem types the fixed taxonomy never listed).
 */
const HzReviewed: React.FC = () => {
  const results = useAdminGet('labs', 'hz_results',
    { run_id: '', reviewed_only: 'true', limit: 1000 });
  const [horizon, setHorizon] = useState('');
  const [ptype, setPtype] = useState('');
  const [approval, setApproval] = useState('');
  const [open, setOpen] = useState<any>(null);

  const rows: any[] = results.data || [];
  const distinct = (key: string) =>
    ['', ...Array.from(new Set(rows.map(r => r[key]).filter(Boolean).map(String))).sort()];
  const horizons = useMemo(() => distinct('horizon'), [rows]);
  const ptypes = useMemo(() => distinct('primary_problem_type'), [rows]);
  const view = useMemo(() => rows.filter(r =>
    (!horizon || String(r.horizon || '') === horizon)
    && (!ptype || String(r.primary_problem_type || '') === ptype)
    && (!approval
      || (approval === 'approved' ? r.approved === true
        : approval === 'rejected' ? r.approved === false
          : r.approved === null || r.approved === undefined))),
    [rows, horizon, ptype, approval]);

  return (
    <Section title="Reviewed technologies" right={
      <div className="flex gap-2 items-center flex-wrap">
        <Select value={horizon} onChange={setHorizon} options={horizons}
          labels={{ '': 'Any horizon' }} />
        <Select value={ptype} onChange={setPtype} options={ptypes}
          labels={{ '': 'Any problem type' }} />
        <Select value={approval} onChange={setApproval}
          options={['', 'approved', 'rejected', 'undecided']}
          labels={{ '': 'Any verdict', undecided: 'no verdict recorded' }} />
        <Btn small onClick={results.reload}>Refresh</Btn>
      </div>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Everything an operator has reviewed, newest first, regardless of which run produced it.
        Open a row to change its verdict.
      </p>
      {results.loading ? <Loading /> : (
        <DataTable rows={view} searchable csvName="horizon_reviewed"
          columns={['reviewed_at', 'utech_technology_id', 'tech_title', 'university_name',
            'horizon', 'primary_problem_type', 'corrected_horizon', 'corrected_problem_type',
            'approved', 'prompt_name', 'model']}
          labels={{ reviewed_at: 'Reviewed', prompt_name: 'Prompt', tech_title: 'Title' }}
          empty={rows.length
            ? 'No reviewed technology matches those filters.'
            : 'Nothing reviewed yet — approve or reject a result on the Run & review tab.'}
          actions={r => <Btn small onClick={() => setOpen(r)}>Open</Btn>} />
      )}
      {open && <HzReview row={open} onClose={() => setOpen(null)} onSaved={results.reload} />}
    </Section>
  );
};

const HzLeaderboard: React.FC = () => {
  const board = useAdminGet('labs', 'hz_leaderboard');
  return (
    <Section title="Prompt leaderboard" right={<Btn small onClick={board.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Which prompt produces the most approved results across all its runs.
      </p>
      {board.loading ? <Loading /> : (
        <DataTable rows={board.data || []} csvName="horizon_leaderboard"
          columns={['prompt', 'runs', 'results', 'reviewed', 'approved', 'approval_pct',
            'horizon_acc_pct', 'problem_acc_pct', 'tokens', 'avg_latency_ms']}
          labels={{ approval_pct: 'Approval %', horizon_acc_pct: 'Horizon acc %', problem_acc_pct: 'Problem acc %' }}
          empty="Nothing scored yet — review some results first." />
      )}
    </Section>
  );
};

const HzRuns: React.FC = () => {
  const runs = useAdminGet('labs', 'hz_runs');
  const t = runs.data?.totals;
  return (
    <div className="flex flex-col gap-4">
      {t && (
        <KPIRow>
          <KPI label="Runs" value={t.runs} />
          <KPI label="Technologies processed" value={t.results?.toLocaleString?.()} />
          <KPI label="Total tokens" value={t.tokens?.toLocaleString?.()} />
          <KPI label="Est. cost" value={`$${t.cost?.toLocaleString?.()}`} />
        </KPIRow>
      )}
      <Section title="Runs" right={<Btn small onClick={runs.reload}>Refresh</Btn>}>
        {runs.loading ? <Loading /> : (
          <DataTable rows={runs.data?.runs || []} searchable csvName="horizon_runs"
            columns={['when', 'prompt', 'model', 'techs', 'tokens', 'avg_cost_per_card',
              'avg_latency_ms', 'reviewed', 'approved', 'approval_pct']} />
        )}
      </Section>
    </div>
  );
};

const HzSummary: React.FC = () => {
  const filters = useAdminGet('labs', 'hz_filters');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const s = useAdminGet('labs', 'hz_summary', { country, industry }, [country, industry]);
  const d = s.data;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <Select value={country} onChange={setCountry}
          options={['', ...(filters.data?.countries || [])]} labels={{ '': 'All countries' }} />
        <Select value={industry} onChange={setIndustry}
          options={['', ...(filters.data?.industries || [])]} labels={{ '': 'All industries' }} />
      </div>
      {s.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Classified" value={d.total?.toLocaleString?.()} />
            <KPI label="Reviewed" value={d.reviewed} />
            <KPI label="Approved" value={d.approved} />
            <KPI label="Approval" value={`${d.approval_pct}%`} />
          </KPIRow>
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="By horizon">
              <BarList items={Object.entries(d.by_horizon || {}).map(([k, v]) => ({ label: k, value: v as number }))} />
            </Section>
            <Section title="Confidence">
              <BarList items={Object.entries(d.by_confidence || {}).map(([k, v]) => ({ label: k, value: v as number }))} />
            </Section>
          </div>
          <Section title="Problem type × Horizon">
            <DataTable rows={d.matrix || []} csvName="horizon_matrix"
              columns={['problem_type', 'H1', 'H2', 'H3', 'total']} />
          </Section>
        </>
      )}
    </div>
  );
};

export default LabsPanel;
